import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import { switchToShop } from "../utils/shopSwitch";
import { unsentSales } from "../pos/utils/storage";
import Modal from "./Modal";

/**
 * Whether the owner's pack plan has room for another shop:
 * `{ shop_limit, pack_size, can_add }`, or null while unknown or not offered.
 * The operator decides the room (the plan's `shop_limit`); the owner uses it.
 */
export const usePackRoom = () => {
  const { isOwner, flag } = useMembership();
  const [room, setRoom] = useState(null);
  const enabled = isOwner && flag("multi_shop");

  useEffect(() => {
    if (!enabled) return;
    shopsAPI
      .newShopRoom()
      .then(({ data }) => setRoom(data))
      .catch(() => setRoom(null));
  }, [enabled]);

  return enabled ? room : null;
};

/**
 * "Open another shop": no form. The shop is made in the pack with a provisional
 * name and this phone switches to it, where the first-run wizard asks for its
 * name and details, then its products (the catalogue, or a copy of this shop's).
 */
const OpenAnotherShop = ({ room, className }) => {
  const { t } = useI18n();
  const { shop } = useMembership();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!room?.can_add) return null;

  const start = async () => {
    setError("");
    // Checked before the shop exists: a switch would refuse to leave these behind
    const waiting = unsentSales();
    if (waiting > 0) {
      setError(t("shops.switcher.unsentSales", { count: waiting }));
      return;
    }

    setBusy(true);
    try {
      const { data } = await shopsAPI.openNewShop();
      await switchToShop(data.shop);
    } catch (err) {
      setBusy(false);
      setError(err.response?.data?.message || t("shops.open.failed"));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ||
          "w-full rounded-lg border-2 border-dashed border-primary-300 px-4 py-3 text-start font-semibold text-primary-700 hover:bg-primary-50"
        }
      >
        ＋ {t("shops.open.button")}
      </button>

      {open &&
        createPortal(
          <Modal
            title={t("shops.open.title")}
            onClose={() => !busy && setOpen(false)}
            maxWidthClass="max-w-md"
            zClass="z-[65]"
          >
            <div className="space-y-3 text-sm text-gray-700">
              <p className="font-medium text-gray-900">
                {t("shops.open.room", {
                  count: room.shop_limit - room.pack_size,
                  limit: room.shop_limit,
                })}
              </p>
              <p>{t("shops.open.intro")}</p>
              <p>{t("shops.open.next", { name: shop?.name || "" })}</p>
              <p className="text-gray-500">{t("shops.open.device")}</p>
            </div>

            {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

            <button
              type="button"
              onClick={start}
              disabled={busy}
              className="mt-5 w-full rounded-lg bg-primary-600 py-3 font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {busy ? t("shops.open.opening") : t("shops.open.confirm")}
            </button>
          </Modal>,
          document.body,
        )}
    </>
  );
};

export default OpenAnotherShop;
